import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useSavedRituals } from '../context/SavedRitualsContext';
import { getRecipe } from '../data/recipes';
import { colorForRitual } from '../data/ritualColors';
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight, Flame, BookOpen } from 'lucide-react';

const MONTH_NAMES = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const WEEKDAY_LABELS = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];

const dateKey = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const RitualCalendar = () => {
  const { completions, savedIds, getStreak, getTotalCompletions } = useSavedRituals();
  const today = useMemo(() => new Date(), []);
  const [month, setMonth] = useState(today.getMonth());
  const [year, setYear] = useState(today.getFullYear());

  useEffect(() => { window.scrollTo(0, 0); }, []);

  // Build a Map of dateKey \u2192 [recipeId, ...] for the visible month.
  const dayMap = useMemo(() => {
    const map = new Map();
    Object.entries(completions).forEach(([recipeId, dates]) => {
      dates.forEach(d => {
        const list = map.get(d) || [];
        list.push(recipeId);
        map.set(d, list);
      });
    });
    return map;
  }, [completions]);

  // Build 6-week grid (starts on Sunday of week containing day 1)
  const grid = useMemo(() => {
    const first = new Date(year, month, 1);
    const startDay = first.getDay(); // 0=Sun
    const gridStart = new Date(year, month, 1 - startDay);
    const cells = [];
    for (let i = 0; i < 42; i++) {
      const d = new Date(gridStart);
      d.setDate(gridStart.getDate() + i);
      cells.push(d);
    }
    return cells;
  }, [year, month]);

  const goPrev = () => {
    let m = month - 1, y = year;
    if (m < 0) { m = 11; y -= 1; }
    setMonth(m); setYear(y);
  };
  const goNext = () => {
    let m = month + 1, y = year;
    if (m > 11) { m = 0; y += 1; }
    setMonth(m); setYear(y);
  };
  const goToday = () => {
    setMonth(today.getMonth());
    setYear(today.getFullYear());
  };

  // Unique rituals shown this month (for legend)
  const monthRituals = useMemo(() => {
    const ids = new Set();
    grid.forEach(d => {
      if (d.getMonth() !== month) return;
      const list = dayMap.get(dateKey(d)) || [];
      list.forEach(id => ids.add(id));
    });
    // Add saved rituals to legend even if no completions this month
    savedIds.forEach(id => ids.add(id));
    return [...ids].map(getRecipe).filter(Boolean);
  }, [grid, dayMap, month, savedIds]);

  // Month totals
  const monthStats = useMemo(() => {
    let done = 0;
    const activeDays = new Set();
    grid.forEach(d => {
      if (d.getMonth() !== month) return;
      const list = dayMap.get(dateKey(d)) || [];
      if (list.length > 0) {
        done += list.length;
        activeDays.add(dateKey(d));
      }
    });
    return { done, activeDays: activeDays.size };
  }, [grid, dayMap, month]);

  return (
    <div>
      {/* Hero */}
      <section className="bg-gradient-to-b from-[#efe9db] to-[#faf7f1]">
        <div className="max-w-4xl mx-auto px-6 py-16 md:py-20 text-center">
          <div className="inline-flex items-center gap-2 uppercase tracking-[0.35em] text-xs text-[#7a8b6b] mb-4">
            <CalendarIcon className="w-4 h-4" /> Ritual calendar
          </div>
          <h1 className="font-serif-elegant text-5xl md:text-6xl mb-3">Your Month in Rituals</h1>
          <p className="text-[#4a4640] max-w-2xl mx-auto leading-relaxed">
            Every colored dot is a moment you showed up for yourself.
          </p>
        </div>
      </section>

      <div className="max-w-5xl mx-auto px-6 lg:px-10 py-12">
        {/* Toolbar */}
        <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
          <div className="flex items-center gap-4">
            <button onClick={goPrev} aria-label="Previous month" className="w-9 h-9 flex items-center justify-center border border-[#c9c1ad] hover:border-[#3a4a3b]">
              <ChevronLeft className="w-4 h-4" />
            </button>
            <h2 className="font-serif-elegant text-2xl md:text-3xl min-w-[220px] text-center">
              {MONTH_NAMES[month]} {year}
            </h2>
            <button onClick={goNext} aria-label="Next month" className="w-9 h-9 flex items-center justify-center border border-[#c9c1ad] hover:border-[#3a4a3b]">
              <ChevronRight className="w-4 h-4" />
            </button>
            <button onClick={goToday} className="text-xs uppercase tracking-widest text-[#3a4a3b] link-underline ml-2">
              Today
            </button>
          </div>
          <div className="flex items-center gap-6 text-xs uppercase tracking-widest text-[#6f6a5f]">
            <span><span className="font-serif-elegant text-lg text-[#2f2a24] not-italic">{monthStats.done}</span> rituals done</span>
            <span><span className="font-serif-elegant text-lg text-[#2f2a24] not-italic">{monthStats.activeDays}</span> active days</span>
          </div>
        </div>

        {/* Weekday header */}
        <div className="grid grid-cols-7 gap-2 mb-2">
          {WEEKDAY_LABELS.map(w => (
            <div key={w} className="text-[10px] uppercase tracking-widest text-[#6f6a5f] text-center py-2">{w}</div>
          ))}
        </div>

        {/* Grid */}
        <div className="grid grid-cols-7 gap-2">
          {grid.map((d, i) => {
            const inMonth = d.getMonth() === month;
            const isToday = dateKey(d) === dateKey(today);
            const list = dayMap.get(dateKey(d)) || [];
            return (
              <div
                key={i}
                className={`min-h-[80px] md:min-h-[100px] p-2 border ${isToday ? 'border-[#3a4a3b] ring-2 ring-[#3a4a3b]/20' : 'border-[#e5decf]'} ${inMonth ? 'bg-[#faf7f1]' : 'bg-transparent opacity-40'} flex flex-col`}
              >
                <div className={`text-xs mb-2 ${isToday ? 'text-[#3a4a3b] font-medium' : 'text-[#4a4640]'}`}>
                  {d.getDate()}
                </div>
                {list.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-auto">
                    {list.slice(0, 6).map(recipeId => {
                      const c = colorForRitual(recipeId);
                      const rec = getRecipe(recipeId);
                      return (
                        <span
                          key={recipeId}
                          title={rec?.title || recipeId}
                          className="w-3 h-3 rounded-full"
                          style={{ backgroundColor: c.bg, boxShadow: `0 0 0 1px ${c.ring}` }}
                        />
                      );
                    })}
                    {list.length > 6 && (
                      <span className="text-[9px] text-[#6f6a5f]">+{list.length - 6}</span>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Legend */}
        <div className="mt-10">
          <div className="uppercase tracking-widest text-xs text-[#7a8b6b] mb-4">Your rituals</div>
          {monthRituals.length === 0 ? (
            <div className="bg-[#efe9db] p-8 text-center">
              <p className="font-serif-elegant text-2xl mb-2">Nothing tracked yet</p>
              <p className="text-[#4a4640] text-sm mb-6">Save a ritual and mark it done to start seeing your calendar bloom.</p>
              <Link to="/recipes" className="btn-primary inline-flex items-center gap-2">
                <BookOpen className="w-4 h-4" /> Browse the Recipe Journal
              </Link>
            </div>
          ) : (
            <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {monthRituals.map(r => {
                const c = colorForRitual(r.id);
                const streak = getStreak(r.id);
                const total = getTotalCompletions(r.id);
                return (
                  <li key={r.id}>
                    <Link
                      to={`/recipes/${r.id}`}
                      className="flex items-center gap-3 p-3 bg-[#faf7f1] border border-[#e5decf] hover:shadow-md transition-shadow"
                    >
                      <span
                        className="w-4 h-4 rounded-full shrink-0"
                        style={{ backgroundColor: c.bg, boxShadow: `0 0 0 1px ${c.ring}` }}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="font-serif-elegant text-lg leading-tight truncate">{r.title}</div>
                        <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-[#6f6a5f]">
                          <span>{total} done</span>
                          {streak > 0 && (
                            <span className="inline-flex items-center gap-0.5 text-[#7a3b2e]">
                              <Flame className="w-3 h-3 fill-current" /> {streak}
                            </span>
                          )}
                        </div>
                      </div>
                    </Link>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

export default RitualCalendar;
